import React, { useState, useEffect, useRef, createContext, useContext ,useMemo } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from './supabase';
import PhemeLanding from './landingPage';
import Guidelines from './components/Guidelines';
import PrivacyPolicy from './components/privacyPolicy';
import { GuestProfileView } from './components/GuestProfileView';
import { useNetworkStatus, isNetworkError } from './utils/networkDetection';




// ==================== AUTH CONTEXT ====================
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const isOnline = useNetworkStatus();
  const wasOfflineRef = useRef(false);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      // Handle invalid refresh token errors
      if (error && (error.message?.includes('Invalid Refresh Token') || error.message?.includes('Refresh Token Not Found'))) {
        console.log('Invalid refresh token detected, clearing session');
        supabase.auth.signOut().catch(() => {
          // Ignore sign out errors
        });
        // Explicitly clear localStorage tokens
        try {
          const keysToRemove = [];
          for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key && key.startsWith('sb-')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach(key => localStorage.removeItem(key));
        } catch (storageError) {
          console.warn('Error clearing localStorage:', storageError);
        }
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }
      
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session.user.id);
      } else {
        setProfile(null);
        setLoading(false);
        setProfileError(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Track offline state
  useEffect(() => {
    if (!isOnline) {
      wasOfflineRef.current = true;
    }
  }, [isOnline]);

  // Retry loading profile when network comes back online
  useEffect(() => {
    if (isOnline && user && wasOfflineRef.current) {
      // We just came back online after being offline
      wasOfflineRef.current = false;
      
      // If we had a network error, always retry when network comes back
      if (profileError && isNetworkError(profileError)) {
        console.log('Network restored, retrying profile load after network error');
        setProfileError(null);
        loadProfile(user.id);
      } 
      // If we don't have a profile but have a user, try loading it
      else if (!profile && profileError) {
        console.log('Network restored, retrying profile load (no profile)');
        setProfileError(null);
        loadProfile(user.id);
      }
      // If we have a profile, refresh it silently to ensure it's up to date
      else if (profile) {
        // Silently refresh profile when network comes back to ensure it's current
        console.log('Network restored, refreshing profile');
        loadProfile(user.id, true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, user]);

  const loadProfile = async (userId, silent = false) => {
    try {
      if (!silent) {
        setProfileError(null);
      }
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error('Error loading profile:', error);
        if (!silent) {
          setProfileError(error);
        }
        // Don't clear existing profile if there's an error - keep showing what we have
        if (!profile && !silent) {
          setLoading(false);
        }
      } else {
        setProfile(data);
        setProfileError(null);
        if (!silent) {
          setLoading(false);
        }
      }
    } catch (error) {
      console.error('Exception loading profile:', error);
      if (!silent) {
        setProfileError(error);
        if (!profile) {
          setLoading(false);
        }
      }
    }
  };

  // const signUp = async (email, password, username, name) => {
  //   // Sign up user
  //   const { data, error } = await supabase.auth.signUp({
  //     email,
  //     password
  //   });
    
  //   if (error) throw error;

  //   // Create profile
  //   const { error: profileError } = await supabase
  //     .from('profiles')
  //     .insert({
  //       id: data.user.id,
  //       username,
  //       name,
  //       email,
  //       bio: ''
  //     });

  //   if (profileError) throw profileError;
  // };

  const signUp = async (email, password, username, name) => {
    // Check if email already exists
    const { data: existingEmail, error: emailCheckError } = await supabase
      .from('profiles')
      .select('email')
      .ilike('email', email)
      .maybeSingle();
    
    if (emailCheckError) throw emailCheckError;
    
    if (existingEmail) {
      throw new Error('This email already has an account. Please go to the sign in page.');
    }
    
    // Check if username already exists (case-insensitive exact match)
    const { data: existingUser, error: checkError } = await supabase
      .from('profiles')
      .select('username')
      .ilike('username', username)
      .maybeSingle();
    
    if (checkError) throw checkError;
    
    if (existingUser) {
      throw new Error('This username is already taken. Please choose another one.');
    }
    
    // Sign up user
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username: username,
          name: name
        }
      }
    });
    
    if (error) throw error;
  
    // Wait a bit for auth to complete
    await new Promise(resolve => setTimeout(resolve, 1000));
  
    // Create profile with service role bypass (using the trigger instead)
    return data;
  };

  const signIn = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    if (error) throw error;
  };

  const signOut = async () => {
    try {
      // Try to sign out from server
      const { error } = await supabase.auth.signOut();
      
      // Handle errors in response
      if (error) {
        // Ignore "session missing" errors - session may already be cleared
        if (error.message?.includes('session missing') || 
            error.message?.includes('Auth session missing') ||
            error.name === 'AuthSessionMissingError') {
          // Session already cleared, that's fine
          console.log('Session already cleared');
        } else {
          // Log other errors but don't break the flow
          console.warn('Sign out error:', error);
        }
      }
    } catch (error) {
      // Handle thrown errors (like AuthSessionMissingError)
      if (error?.message?.includes('session missing') || 
          error?.message?.includes('Auth session missing') ||
          error?.name === 'AuthSessionMissingError') {
        // Session already cleared, that's fine
        console.log('Session already cleared');
      } else {
        // Log other errors but don't break the flow
        console.warn('Sign out error:', error);
      }
    } finally {
      // CRITICAL FIX: Explicitly clear Supabase's localStorage keys
      // This ensures the session is removed even if signOut() fails
      try {
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          // Remove all Supabase auth tokens (they start with 'sb-')
          if (key && key.startsWith('sb-')) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        console.log('Cleared localStorage auth tokens');
      } catch (storageError) {
        console.warn('Error clearing localStorage:', storageError);
      }
      
      // Always clear local state regardless of errors
      setUser(null);
      setProfile(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, profile, signUp, signIn, signOut, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);




// ==================== AUTH MODAL (Optimized for iPhone) ====================

export const AuthModal = ({ onClose, onNavigateToGuidelines, onNavigateToPrivacyPolicy, onCheckItOut }) => {
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { signUp, signIn } = useAuth();

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');
    setLoading(true);
    
    try {
      if (isSignUp) {
        if (!username.trim() || !name.trim()) {
          setError('Username and name are required');
          setLoading(false);
          return;
        }
        if (!agreedToTerms) {
          setError('You must agree to the terms and conditions');
          setLoading(false);
          return;
        }
        await signUp(email, password, username, name);
        // Show success message after signup
        setSuccessMessage('Click the link in your email to sign in!');
        setLoading(false);
      } else {
        await signIn(email, password);
      }
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // iPhone Optimization: Font size 16px prevents auto-zoom on focus
  const inputStyle = {
    width: '100%',
    padding: '14px 18px',
    marginBottom: '16px',
    border: '1px solid rgba(198,162,91,0.3)',
    background: 'rgba(230,197,126,0.2)',
    fontFamily: "'Montserrat', sans-serif",
    fontSize: '16px', // Vital for iOS
    letterSpacing: '0.02em',
    outline: 'none',
    transition: 'all 0.3s ease',
    color: '#e6c57e',
    backdropFilter: 'blur(5px)',
    WebkitBackdropFilter: 'blur(5px)',
    borderRadius: '999px',
    boxSizing: 'border-box',
    appearance: 'none', // Removes default iOS inner shadow
    WebkitAppearance: 'none'
  };

  const modalContent = (
    <div 
      className="modal-overlay"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(49,35,16,0.6)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        zIndex: 10000,
        animation: 'fadeIn 0.4s ease',
        // Flex layout handles the keyboard popping up better than Absolute positioning
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflowY: 'auto', // Allows scrolling if screen is short
        WebkitOverflowScrolling: 'touch'
      }}
      onClick={onClose}
    >
      <div 
        className="auth-modal-content"
        style={{
          background: 'rgba(174,180,184,0.15)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          padding: '50px 45px',
          maxWidth: '460px',
          width: 'calc(100% - 40px)', // Responsive width with margin
          border: '1px solid rgba(198,162,91,0.25)',
          boxShadow: '0 20px 60px rgba(49,35,16,0.4), inset 0 0 60px rgba(198,162,91,0.08)',
          borderRadius: '16px',
          animation: 'modalZoom 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          boxSizing: 'border-box',
          margin: 'auto', // Centers in flex container
          position: 'relative',
          transform: 'translateZ(0)' // Hardware acceleration
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button 
          onClick={onClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '10px', // Moved up slightly
            right: '10px', // Moved right slightly
            fontSize: '32px', // Larger touch target
            width: '44px', // Min Apple touch target size
            height: '44px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#c6a25b',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
            fontWeight: 300,
            transition: 'all 0.3s ease',
            lineHeight: 1,
            zIndex: 10
          }}
        >
          ×
        </button>

        <div style={{ textAlign: 'center', marginBottom: '35px' }}>
          <h1 className="auth-modal-title" style={{
            fontFamily: "'Apple Garamond', Garamond, 'Times New Roman', serif",
            fontSize: '3.5em',
            color: '#e6c57e',
            letterSpacing: '0.08em',
            fontWeight: 400,
            margin: '0 0 10px 0',
            textShadow: '2px 2px 10px rgba(49,35,16,0.5)',
            lineHeight: '1.1'
          }}>
            dr[a]fts
          </h1>
          <p style={{
            fontFamily: "'Montserrat', sans-serif",
            fontSize: '0.85em',
            letterSpacing: '0.05em',
            color: '#c6a25b',
            fontWeight: 300,
            marginBottom: '20px',
            marginTop: 0
          }}>
            social for humans
          </p>
          <div style={{
            width: '60px',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, #af731f, transparent)',
            margin: '20px auto 0',
            boxShadow: '0 0 10px rgba(175,115,31,0.5)'
          }}></div>
        </div>

        <div style={{ 
          display: 'flex', 
          gap: '8px', 
          marginBottom: '30px',
          borderBottom: '1px solid rgba(198,162,91,0.2)'
        }}>
          <button
            onClick={() => {
              setIsSignUp(false);
              setAgreedToTerms(false);
              setSuccessMessage('');
              setError('');
            }}
            style={{
              flex: 1,
              padding: '12px',
              background: 'none',
              border: 'none',
              borderBottom: !isSignUp ? '2px solid #c6a25b' : '2px solid transparent',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '13px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              color: !isSignUp ? '#e6c57e' : 'rgba(198,162,91,0.6)',
              fontWeight: !isSignUp ? 400 : 300,
              transition: 'all 0.3s ease'
            }}
          >
            Sign In
          </button>
          <button
            onClick={() => {
              setIsSignUp(true);
              setAgreedToTerms(false);
              setSuccessMessage('');
              setError('');
            }}
            style={{
              flex: 1,
              padding: '12px',
              background: 'none',
              border: 'none',
              borderBottom: isSignUp ? '2px solid #c6a25b' : '2px solid transparent',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '13px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              color: isSignUp ? '#e6c57e' : 'rgba(198,162,91,0.6)',
              fontWeight: isSignUp ? 400 : 300,
              transition: 'all 0.3s ease'
            }}
          >
            Sign Up
          </button>
        </div>

        <form onSubmit={handleSubmit} style={{ width: '100%', margin: 0, padding: 0 }}>
          {isSignUp && (
            <>
              <input
                type="text"
                placeholder="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={inputStyle}
                // Added autoComplete for better UX
                autoComplete="name"
                onFocus={(e) => {
                  e.target.style.borderColor = '#c6a25b';
                  e.target.style.background = 'rgba(230,197,126,0.25)';
                  e.target.style.boxShadow = '0 0 15px rgba(198,162,91,0.3)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(198,162,91,0.3)';
                  e.target.style.background = 'rgba(230,197,126,0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
              <input
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={inputStyle}
                autoComplete="username"
                autoCapitalize="none"
                onFocus={(e) => {
                  e.target.style.borderColor = '#c6a25b';
                  e.target.style.background = 'rgba(230,197,126,0.25)';
                  e.target.style.boxShadow = '0 0 15px rgba(198,162,91,0.3)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(198,162,91,0.3)';
                  e.target.style.background = 'rgba(230,197,126,0.2)';
                  e.target.style.boxShadow = 'none';
                }}
              />
            </>
          )}
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            autoComplete="email"
            autoCapitalize="none"
            onFocus={(e) => {
              e.target.style.borderColor = '#c6a25b';
              e.target.style.background = 'rgba(230,197,126,0.25)';
              e.target.style.boxShadow = '0 0 15px rgba(198,162,91,0.3)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(198,162,91,0.3)';
              e.target.style.background = 'rgba(230,197,126,0.2)';
              e.target.style.boxShadow = 'none';
            }}
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
            autoComplete={isSignUp ? "new-password" : "current-password"}
            onFocus={(e) => {
              e.target.style.borderColor = '#c6a25b';
              e.target.style.background = 'rgba(230,197,126,0.25)';
              e.target.style.boxShadow = '0 0 15px rgba(198,162,91,0.3)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = 'rgba(198,162,91,0.3)';
              e.target.style.background = 'rgba(230,197,126,0.2)';
              e.target.style.boxShadow = 'none';
            }}
          />
          
          {error && (
            <div style={{ 
              color: '#e6c57e', 
              fontSize: '12px', 
              marginBottom: '16px',
              padding: '8px',
              background: 'rgba(255, 0, 0, 0.1)', // Slight highlight for error visibility
              borderRadius: '4px',
              border: '1px solid rgba(255, 0, 0, 0.2)',
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: '0.02em',
              textShadow: '1px 1px 5px rgba(49,35,16,0.7)'
            }}>
              {error}
            </div>
          )}

          {successMessage && (
            <div style={{ 
              color: '#e6c57e', 
              fontSize: '13px', 
              marginBottom: '16px',
              padding: '10px',
              background: 'rgba(175, 115, 31, 0.2)',
              borderRadius: '4px',
              border: '1px solid rgba(198, 162, 91, 0.3)',
              fontFamily: "'Montserrat', sans-serif",
              letterSpacing: '0.02em',
              textAlign: 'center',
              textShadow: '1px 1px 5px rgba(49,35,16,0.7)'
            }}>
              {successMessage}
            </div>
          )}

          {isSignUp && (
            <div style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '20px',
              marginTop: '5px',
              gap: '12px' // Increased gap for touch
            }}>
              <input
                type="checkbox"
                id="terms-checkbox"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                style={{
                  marginTop: '2px',
                  width: '22px', // Larger hit area for checkbox
                  height: '22px',
                  cursor: 'pointer',
                  accentColor: '#c6a25b',
                  flexShrink: 0
                }}
              />
              <label
                htmlFor="terms-checkbox"
                style={{
                  fontFamily: "'Montserrat', sans-serif",
                  fontSize: '12px',
                  color: '#e6c57e',
                  letterSpacing: '0.02em',
                  lineHeight: '1.5',
                  cursor: 'pointer',
                  userSelect: 'none',
                  paddingTop: '2px'
                }}
              >
                I agree to the{' '}
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigateToGuidelines) onNavigateToGuidelines();
                  }}
                  style={{ color: '#c6a25b', textDecoration: 'underline', padding: '5px 0' }}
                >
                  terms and conditions
                </span>
                {' '}and{' '}
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    if (onNavigateToPrivacyPolicy) onNavigateToPrivacyPolicy();
                  }}
                  style={{ color: '#c6a25b', textDecoration: 'underline', padding: '5px 0' }}
                >
                  privacy policy
                </span>
              </label>
            </div>
          )}
          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '16px 18px', // Taller button for easy tapping
              background: loading ? 'rgba(175,115,31,0.2)' : 'rgba(175,115,31,0.3)',
              color: '#e6c57e',
              border: '1px solid rgba(198,162,91,0.4)',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '14px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.4s ease',
              marginBottom: '10px',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderRadius: '999px',
              fontWeight: 400,
              textShadow: '1px 1px 3px rgba(49,35,16,0.5)',
              boxSizing: 'border-box',
              // App Store Compliance: Ensure button is easily distinguishable
              boxShadow: '0 4px 15px rgba(0,0,0,0.2)' 
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.target.style.background = 'rgba(175,115,31,0.6)';
                e.target.style.borderColor = '#c6a25b';
                e.target.style.boxShadow = '0 0 20px rgba(198,162,91,0.4)';
                e.target.style.transform = 'translateY(-2px)';
              }
            }}
            onMouseOut={(e) => {
              e.target.style.background = loading ? 'rgba(175,115,31,0.2)' : 'rgba(175,115,31,0.3)';
              e.target.style.borderColor = 'rgba(198,162,91,0.4)';
              e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.2)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            {loading ? 'Loading...' : (isSignUp ? 'Create Account' : 'Sign In')}
          </button>
        </form>

        {/* Check it out first button */}
        <div style={{
          marginTop: '20px',
          paddingTop: '20px',
          borderTop: '1px solid rgba(198,162,91,0.2)',
          textAlign: 'center'
        }}>
          <button
            type="button"
            onClick={() => {
              if (onCheckItOut) {
                onCheckItOut();
              }
            }}
            style={{
              width: '100%',
              padding: '14px 18px',
              background: 'transparent',
              color: '#c6a25b',
              border: '1px solid rgba(198,162,91,0.4)',
              fontFamily: "'Montserrat', sans-serif",
              fontSize: '13px',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: 'pointer',
              transition: 'all 0.4s ease',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)',
              borderRadius: '999px',
              fontWeight: 300,
              textShadow: '1px 1px 3px rgba(49,35,16,0.5)',
              boxSizing: 'border-box'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(198,162,91,0.2)';
              e.target.style.borderColor = '#c6a25b';
              e.target.style.transform = 'translateY(-2px)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'transparent';
              e.target.style.borderColor = 'rgba(198,162,91,0.4)';
              e.target.style.transform = 'translateY(0)';
            }}
          >
            or check it out first!
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes modalZoom {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        
        input::placeholder {
          color: rgba(198,162,91,0.6);
        }

        /* Remove iOS tap highlight color */
        button, input, label, span {
          -webkit-tap-highlight-color: transparent;
        }
        
        /* SCROLLBAR STYLING (For the modal overlay) */
        .modal-overlay::-webkit-scrollbar {
          width: 8px;
        }
        .modal-overlay::-webkit-scrollbar-track {
          background: rgba(49,35,16,0.3);
        }
        .modal-overlay::-webkit-scrollbar-thumb {
          background: rgba(198,162,91,0.3);
          border-radius: 4px;
        }

        /* MOBILE SPECIFIC OVERRIDES */
        @media (max-width: 600px) {
          .modal-overlay {
            /* Ensure padding accounts for Home Indicator and safe areas */
            padding-top: env(safe-area-inset-top); 
            padding-bottom: calc(20px + env(safe-area-inset-bottom)); 
            padding-left: 15px;
            padding-right: 15px;
            box-sizing: border-box;
            align-items: flex-start; /* Align top on mobile so keyboard doesn't hide top */
          }
          
          .auth-modal-content {
            margin-top: 40px; /* Top margin to prevent hugging the notch */
            margin-bottom: 40px;
            padding: 35px 25px !important;
          }

          .auth-modal-title {
            font-size: 2.5em !important;
          }
        }
        
        /* LANDSCAPE MODE ON IPHONE */
        @media (max-height: 500px) and (orientation: landscape) {
           .modal-overlay {
             align-items: flex-start;
           }
           .auth-modal-content {
             margin-top: 20px;
             margin-bottom: 20px;
           }
        }
      `}</style>
    </div>
  );

  return ReactDOM.createPortal(modalContent, document.body);
};
// Please ensure the useAuth hook and ReactDOM are available in your environment.







// ==================== ONBOARDING ====================
export const Onboarding = ({ onAuthSuccess }) => {
  const [showAuth, setShowAuth] = useState(false);
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showGuestView, setShowGuestView] = useState(false);
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#e6c57e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          color: '#553918',
          fontSize: '0.85em',
          letterSpacing: '0.15em',
          textTransform: 'uppercase',
          fontWeight: 300
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (user) {
    if (onAuthSuccess) onAuthSuccess();
    return null;
  }

  // Show guest profile view if requested
  if (showGuestView) {
    return <GuestProfileView onBack={() => {
      setShowGuestView(false);
      setShowAuth(true);
    }} />;
  }

  // Show privacy policy if requested
  if (showPrivacyPolicy) {
    return <PrivacyPolicy onBack={() => setShowPrivacyPolicy(false)} />;
  }

  // Show guidelines if requested
  if (showGuidelines) {
    return <Guidelines onBack={() => setShowGuidelines(false)} />;
  }

  return (
    <>
      <PhemeLanding onGetStarted={() => setShowAuth(true)} />
      {showAuth && (
        <AuthModal 
          onClose={() => setShowAuth(false)} 
          onNavigateToGuidelines={() => {
            setShowAuth(false);
            setShowGuidelines(true);
          }}
          onNavigateToPrivacyPolicy={() => {
            setShowAuth(false);
            setShowPrivacyPolicy(true);
          }}
          onCheckItOut={() => {
            setShowAuth(false);
            setShowGuestView(true);
          }}
        />
      )}
    </>
  );
};